/**
 * What changed about the SHAPE of protection, as opposed to its outcome.
 *
 * Sits in Activity beside Status changes, and the pairing is the point: that
 * page answers "what did the backup do?", this one answers "is it still
 * backing up what it used to?". They come apart silently — drop a volume from
 * an agent and every subsequent backup succeeds, on schedule, green on the
 * board, protecting less than it did yesterday.
 *
 * SERIOUS IS NOT AN ALARM, and the copy has to keep saying so. Nothing here
 * has changed a machine's status and nothing has emailed anybody: these are
 * changes somebody probably made on purpose, listed so that the ones nobody
 * made get noticed. A page that shouted would be muted within a week.
 */

import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import { errText } from './btApi';
import { useCan } from './org';

function call<TIn extends object, TOut>(name: string) {
  return async (data: TIn): Promise<TOut> => (await httpsCallable<TIn, TOut>(functions, name)(data)).data;
}

type Kind =
  | 'protection-removed' | 'protection-added'
  | 'volume-removed' | 'volume-added'
  | 'backup-disabled' | 'backup-enabled'
  | 'destination-changed' | 'schedule-changed' | 'plan-changed';

type ReviewState = 'new' | 'acknowledged' | 'issue';

interface Change {
  id: string;
  targetId: string;
  source: string;
  name: string;
  client: string;
  kind: Kind;
  severity: 'serious' | 'info';
  summary: string;
  from: string;
  to: string;
  atMs: number;
  state: ReviewState;
  stateBy: string;
  stateAtMs: number;
}

const listChanges = call<{ orgId: string }, { changes: Change[] }>('listDefinitionChanges');
const setState =
  call<{ orgId: string; id: string; state: ReviewState }, { ok: boolean }>('setDefinitionChangeState');

/** Short label for the chip. The summary carries the meaning. */
const KIND_LABEL: Record<Kind, string> = {
  'protection-removed': 'Job removed',
  'protection-added': 'Job added',
  'volume-removed': 'Volume dropped',
  'volume-added': 'Volume added',
  'backup-disabled': 'Backup switched off',
  'backup-enabled': 'Backup switched on',
  'destination-changed': 'Destination changed',
  'schedule-changed': 'Schedule changed',
  'plan-changed': 'Plan changed',
};

function when(ms: number): string {
  if (!ms) return '';
  const d = new Date(ms);
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function ChangesPanel({ orgId }: { orgId: string }) {
  const [rows, setRows] = useState<Change[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [onlySerious, setOnlySerious] = useState(false);
  /*
   * DEFAULTS TO HIDING WHAT HAS BEEN DEALT WITH, not to hiding nothing.
   *
   * The queue's job is to show what nobody has looked at. Left showing
   * everything, a week of acknowledged changes buries the one that arrived
   * this morning, and the page stops being read - which is the same failure
   * as a false alarm, reached from the other direction.
   */
  const [showDone, setShowDone] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const can = useCan();
  const canReview = can('changes.review');

  const load = useCallback(async () => {
    setErr(null);
    try {
      const r = await listChanges({ orgId });
      setRows(r.changes);
    } catch (e) {
      setErr(errText(e));
      setRows([]);
    }
  }, [orgId]);

  useEffect(() => { void load(); }, [load]);

  async function mark(c: Change, state: ReviewState) {
    setBusy(c.id); setErr(null);
    try {
      await setState({ orgId, id: c.id, state });
      // Patched in place rather than reloaded: re-fetching 500 rows to change
      // one word makes the row you just clicked jump under the cursor.
      setRows((prev) => (prev ?? []).map((r) => (r.id === c.id ? { ...r, state } : r)));
    } catch (e) {
      setErr(errText(e));
    } finally { setBusy(null); }
  }

  const all = rows ?? [];
  const shown = all.filter((c) => {
    if (onlySerious && c.severity !== 'serious') return false;
    // An issue stays visible whatever the filter says. It is the one state
    // somebody has explicitly said still needs work.
    if (!showDone && c.state === 'acknowledged') return false;
    return true;
  });
  const seriousCount = all.filter((c) => c.severity === 'serious').length;
  const openCount = all.filter((c) => c.state === 'new').length;
  const issueCount = all.filter((c) => c.state === 'issue').length;
  const doneCount = all.filter((c) => c.state === 'acknowledged').length;

  return (
    <section className="panel">
      <div className="row-between">
        <div>
          <h3 className="panel-title">Configuration changes</h3>
          <p className="panel-note" style={{ marginBottom: 0 }}>
            What changed about what is being backed up — jobs, volumes, destinations and schedules —
            rather than whether a backup worked. Most of these will be changes somebody made on
            purpose. They are listed so the ones nobody made get noticed.
          </p>
        </div>
      </div>

      {err && <div className="banner banner-error">{err}</div>}

      {rows === null ? (
        <div className="empty">Loading.</div>
      ) : rows.length === 0 ? (
        <div className="empty">
          Nothing recorded yet. A machine is compared against its own previous sweep, so the first
          time we see one it sets a baseline quietly and reports nothing — changes appear from the
          next sweep onwards.
        </div>
      ) : (
        <>
          <div className="row" style={{ gap: 16, flexWrap: 'wrap', margin: '10px 0' }}>
            <label className="check-row">
              <input type="checkbox" checked={onlySerious} onChange={(e) => setOnlySerious(e.target.checked)} />
              <span>Only changes that mean less is protected ({seriousCount})</span>
            </label>
            <label className="check-row">
              <input type="checkbox" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} />
              <span>Include acknowledged ({doneCount})</span>
            </label>
            <span className="dim" style={{ fontSize: 12 }}>
              {openCount} not yet looked at{issueCount > 0 ? `, ${issueCount} marked as an issue` : ''}
            </span>
          </div>

          <table className="mini-table">
            <thead>
              <tr>
                <th>When</th><th>Machine</th><th>Client</th><th>Change</th><th>What it means</th><th>Was</th><th>Now</th><th>Review</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((c) => (
                <tr key={c.id}>
                  <td className="dim" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{when(c.atMs)}</td>
                  <td style={{ fontSize: 12 }}>{c.name}</td>
                  <td className="dim" style={{ fontSize: 12 }}>{c.client || 'unassigned'}</td>
                  <td>
                    <span className={'tag ' + (c.severity === 'serious' ? 'tag-bad' : '')}>
                      {KIND_LABEL[c.kind] ?? c.kind}
                    </span>
                  </td>
                  <td style={{ fontSize: 12 }}>{c.summary}</td>
                  {/* The before and after are the evidence. A change somebody
                      cannot check for themselves is a change they have to take
                      on trust, which is not what this page is for. */}
                  <td className="mono dim" style={{ fontSize: 11 }}>{c.from}</td>
                  <td className="mono" style={{ fontSize: 11 }}>{c.to}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {c.state !== 'new' && (
                      <div>
                        <span className={'tag ' + (c.state === 'issue' ? 'tag-bad' : 'tag-ok')}>
                          {c.state === 'issue' ? 'Issue' : 'Acknowledged'}
                        </span>
                        {/* WHO decided, not just what was decided. "It was
                            acknowledged" is half an answer; the other half is
                            who to ask about it. */}
                        {c.stateBy && (
                          <div className="dim" style={{ fontSize: 10, marginTop: 2 }}>{c.stateBy}</div>
                        )}
                      </div>
                    )}
                    {canReview && (
                      <div className="row" style={{ gap: 6, marginTop: c.state === 'new' ? 0 : 4 }}>
                        {c.state !== 'acknowledged' && (
                          <button
                            className="btn-link" style={{ fontSize: 11 }} disabled={busy === c.id}
                            title="I have seen this and it is expected"
                            onClick={() => void mark(c, 'acknowledged')}
                          >
                            Acknowledge
                          </button>
                        )}
                        {c.state !== 'issue' && (
                          <button
                            className="btn-link" style={{ fontSize: 11 }} disabled={busy === c.id}
                            title="This is a problem and needs work"
                            onClick={() => void mark(c, 'issue')}
                          >
                            Mark as issue
                          </button>
                        )}
                        {c.state !== 'new' && (
                          <button
                            className="btn-link dim" style={{ fontSize: 11 }} disabled={busy === c.id}
                            title="Put it back in the queue"
                            onClick={() => void mark(c, 'new')}
                          >
                            Undo
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {shown.length === 0 && (
            <div className="dim" style={{ fontSize: 12, marginTop: 8 }}>
              {showDone
                ? 'No changes in this period reduced what is protected.'
                : 'Nothing left to look at. Tick "Include acknowledged" to see what has been dealt with - nothing is ever deleted.'}
            </div>
          )}
        </>
      )}
    </section>
  );
}
